import type { AppSearchBoxProps, MenuDefinition, MenuItemControlProps, NextAppSearchBoxProps, RibbonButtonProps, RibbonFlyoutAnchorProps, RibbonSplitButtonProps, RibbonToggleButtonProps, AppButtonProps, AppFlyoutAnchorProps, } from "owa-acui";
import { createAppButton, createAppFlyoutAnchor, createAppSearchBox, createAppSplitButton, createGeneratedToggleMenuItem, getRibbonIconColor } from "owa-command-ribbon";
import { getDefaultRibbonStyles, getDefaultRibbonStylesForSelected, } from "owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles";
import Checkmark from "owa-fluent-icons-svg/lib/icons/CheckmarkFilled";
import type { ClientItemId } from "owa-client-ids";
import type { IButtonStyles, IKeytipProps } from "@fluentui/react";
import { type MailRibbonControlId } from "owa-ribbon-ids/lib/mailRibbonId";
import type { MailboxInfo } from "owa-client-types";
import type { RibbonControlDefinitionProps } from "./RibbonControlDefinitionProps";
import { getIsReadButtonDisabled } from "./getIsReadButtonDisabled";
import { getRibbonMailboxInfo } from "./getRibbonMailboxInfo";

/**
 * creates RibbonButtonProps object for use with MailRibbon
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param tooltip Hover tooltip
 * @param props control props
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object
 * @param itemId Optional itemId, used in read pop outs
 * @param overrides Optional object/array of other RibbonButtonProps props to override.
 */
export const createMailAppButton = (id: number | string, label: string, tooltip: string | undefined, props: RibbonControlDefinitionProps, onExecute: () => void, keytipProps?: IKeytipProps, icon?: string, iconColor?: string, styles?: IButtonStyles, itemId?: ClientItemId, overrides?: Partial<AppButtonProps>): RibbonButtonProps => {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    return createAppButton({
        id,
        label,
        onExecute,
        keytipProps,
        icon,
        iconColor,
        styles: styles ?? getDefaultRibbonStyles(),
        mailboxInfo,
        overrides: {
            customTooltip: tooltip,
            disabled: getIsReadButtonDisabled(id, props.isPopout, mailboxInfo, itemId),
            ...overrides,
        },
    });
};

/**
 * creates RibbonButtonProps object for use with MailRibbon
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param tooltip Hover tooltip
 * @param props control props
 * @param menuDefinition Object defining the menu shown when the flyout is invoked
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object
 * @param itemId Optional itemId, used in read pop outs
 * @param overrides Optional object/array of other RibbonFlyoutAnchorProps props to override.
 */
export const createMailAppFlyoutAnchor = (id: number | string, label: string, tooltip: string | undefined, props: RibbonControlDefinitionProps, menuDefinition: MenuDefinition, mailboxInfo: MailboxInfo, keytipProps?: IKeytipProps, icon?: string, iconColor?: string, styles?: IButtonStyles, itemId?: ClientItemId, overrides?: Partial<AppFlyoutAnchorProps>): RibbonFlyoutAnchorProps => {
    return createAppFlyoutAnchor(id, label, menuDefinition, mailboxInfo, keytipProps, icon, iconColor, styles ?? getDefaultRibbonStyles(), {
        customTooltip: tooltip,
        disabled: getIsReadButtonDisabled(id, props.isPopout, getRibbonMailboxInfo(props.mailboxInfo), itemId),
        ...overrides,
    });
};

/**
 * creates RibbonButtonProps object for use with MailRibbon
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param splitButtonAriaLabel Accessible label for the dropdown chevron button.
 * @param tooltip Hover tooltip
 * @param props control props
 * @param menuDefinition Object defining the menu shown when the split button chevron is invoked
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object
 * @param itemId Optional itemId, used in read pop outs
 * @param overrides Optional object of other RibbonSplitButtonProps props to override.
 * @param isHero Indicates a "Hero" style button in SLR
 */
export const createMailAppSplitButton = (id: number | string, label: string, splitButtonAriaLabel: string | undefined, tooltip: string | undefined, props: RibbonControlDefinitionProps, menuDefinition: MenuDefinition, onExecute: NonNullable<RibbonSplitButtonProps["onExecute"]>, keytipProps?: IKeytipProps, icon?: string, iconColor?: string, styles?: IButtonStyles, itemId?: ClientItemId, overrides: Partial<RibbonSplitButtonProps> = {}, isHero?: boolean): RibbonSplitButtonProps => {
    return createAppSplitButton(id, label, menuDefinition, onExecute, getRibbonMailboxInfo(props.mailboxInfo), keytipProps, icon, iconColor, styles ?? getDefaultRibbonStyles(), {
        customTooltip: tooltip,
        disabled: getIsReadButtonDisabled(id, props.isPopout, getRibbonMailboxInfo(props.mailboxInfo), itemId),
        splitButtonAriaLabel,
        ...overrides,
    }, isHero);
};

/**
 * creates MenuItemControlProps object for use with the WAC Ribbon package. Used for generated menu items (i.e. from a custom list of categories)
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed ot the user
 * @param tooltip Optional Hover tooltip
 * @param onExecute Function invoked when the button is executed
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param keytipProps Optional keytip for button
 * @param isSelected boolean. When `true`, a checkmark icon will be used
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time. Defaults to checkmark if not provide and isSelected is true.
 * @param iconColor Optional icon foreground color override. Defaults to green if color not provided and isSelected is true.
 * @param styles Optional styles object.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createMailGeneratedMenuItem = (id: number | string, label: string, tooltip: string | undefined, onExecute: NonNullable<RibbonToggleButtonProps["onExecute"]>, mailboxInfo: MailboxInfo, keytipProps: IKeytipProps | undefined, isSelected: boolean, icon?: string, iconColor?: string, styles?: IButtonStyles, overrides?: Partial<MenuItemControlProps>): MenuItemControlProps => {
    const selectedIconColor = getRibbonIconColor(625);
    
    return createGeneratedToggleMenuItem(id.toString(), label, onExecute, mailboxInfo, isSelected, icon ?? (isSelected ? Checkmark : undefined), iconColor ?? (isSelected ? selectedIconColor : undefined), styles ?? (isSelected ? getDefaultRibbonStylesForSelected() : getDefaultRibbonStyles()), {
        customTooltip: tooltip,
        keytipProps,
        ...overrides,
    });
};

/**
 * creates RibbonFlyoutAnchorProps object for use with MailRibbon
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param tooltip Hover tooltip
 * @param menuDefinition Object defining the menu shown when the flyout is invoked
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param keytipProps Optional keytip for button
 * @param isSelected Optional boolean. If provided, the icon will be displayed only if true.
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time. Defaults to checkmark if not provide and isSelected is true.
 * @param iconColor Optional icon foreground color override. Defaults to green if color not provided and isSelected is true.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createMailGeneratedMenuFlyoutAnchor = (id: number | string, label: string, tooltip: string | undefined, menuDefinition: MenuDefinition, mailboxInfo: MailboxInfo, keytipProps?: IKeytipProps, isSelected?: boolean, icon?: string, iconColor?: string, styles?: IButtonStyles, overrides?: Partial<AppFlyoutAnchorProps>): RibbonFlyoutAnchorProps => {
    return createAppFlyoutAnchor(id, label, menuDefinition, mailboxInfo, keytipProps, icon ?? (isSelected === true ? Checkmark : undefined), iconColor ??
        (isSelected === true
            ? getRibbonIconColor(625)
            : undefined), styles ??
        (isSelected === true ? getDefaultRibbonStylesForSelected() : getDefaultRibbonStyles()), {
        customTooltip: tooltip,
        ...overrides,
    });
};

/**
 * creates AppSearchBoxProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Placeholder text for the search box
 * @param onChangeCallback Callback when the searchbox contents change. Empty box returns "undefined" newValue
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param keytipProps Optional keytip for search box
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param typeProp Optional search box type override
 * @param overrides Optional object of other NextAppSearchBoxProps props to override. ACUI renders
 * the Fluent v9 search box, so these must be v9 props: for example `aria-label` rather than
 * `ariaLabel`, which the v9 component drops.
 */
export const createMailAppSearchBox = (id: number | string, label: string, onChangeCallback: (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => void, mailboxInfo: MailboxInfo, keytipProps?: IKeytipProps, icon?: string, iconColor?: string, typeProp?: string, overrides?: Partial<NextAppSearchBoxProps>): AppSearchBoxProps => {
    return createAppSearchBox(id, label, onChangeCallback, mailboxInfo, keytipProps, icon, iconColor, typeProp, {
        autoComplete: "off",
        ...overrides,
    });
};
